/**
 * Shared Jinja token-detection regexes for the milkdown jinja node family.
 *
 * `jinjaToken` is the first of a planned family of first-class jinja node types
 * (a `jinjaBlock` is planned next), so the detection regex lives here, decoupled
 * from any single node module, and is reused by the mdast transformer, the input
 * rule, and any future sibling nodes.
 *
 * Token forms:
 *  - `{{ expr }}`  — variable / expression
 *  - `{# cmt #}`   — comment
 *  - `{% stmt %}`  — statement / control flow (may span lines)
 *  - `{ident...}`  — single-brace token: a SINGLE identifier with optional
 *    SURROUNDING HORIZONTAL whitespace (`{[ \t]*[A-Za-z_][^{}\s]*[ \t]*}`), so
 *    `{visit_type}`, `{ visit_type }`, `{patient[0]}` tokenize but multi-word
 *    prose like `{note to self}` (INTERNAL whitespace) and newline-padded
 *    `{\nname\n}` stay literal text. A `(?<!{)`
 *    lookbehind keeps it from matching the tail of a `{{ … }}`: while live-typing
 *    `{{foo}}`, the half-typed `{{foo}` must NOT fire as the single-brace `{foo}`
 *    (which would orphan the leading `{`); the rule waits for the full `}}`.
 *
 * Each multi-char form requires at least one non-whitespace content char, so
 * empty `{}` and whitespace-only `{{ }}` / `{# #}` / `{% %}` stay literal text.
 * Unterminated tokens (`{{ foo`) never match — both delimiters are required.
 * The `{{ }}` / `{# #}` arms gate content with a lookahead bounded by their
 * single closing brace. The `{% %}` arm needs a leading non-whitespace char
 * that is NOT the closing `%}` (`\s*(?!%\})\S`) so whitespace-only `{% %}` stays
 * literal, and a body that cannot cross its own `%}` (`(?:[^%]|%(?!\}))*`). The
 * body deliberately still admits bare `%` / `}` (modulo, dict literals like
 * `{% set d = {} %}`) and newlines, but stopping at the first `%}` keeps the
 * anchored input-rule variant from matching back across an earlier token on the
 * same line (the `$`-anchor would otherwise stretch to the line's last `%}`).
 */

/** Body of a single-brace Fact token: one identifier, no whitespace, no braces. */
const FACT_BODY_SOURCE = '(?!snippet:)[A-Za-z_][^{}\\s]*'

/** Source fragments for each token form, ordered double-brace → single-brace. */
const TOKEN_FORM_SOURCES = [
  '\\{\\{(?=[^}]*[^}\\s])[^}]*\\}\\}', // {{ expr }}
  '\\{#(?=[^}]*[^#}\\s])[^}]*#\\}', // {# comment #}
  '\\{%\\s*(?!%\\})\\S(?:[^%]|%(?!\\}))*%\\}', // {% statement %} (may span lines)
  `(?<!\\{)\\{[ \\t]*${FACT_BODY_SOURCE}[ \\t]*\\}`, // {single_brace_token} (pad h-ws)
] as const

const FACT_BODY_REGEX = new RegExp(`^${FACT_BODY_SOURCE}$`, 'i')

/**
 * True if `text` is a valid single-brace Fact token BODY (delimiters excluded).
 *
 * The completed-token forms above tolerate SURROUNDING horizontal padding
 * (`{ name }`), which the toolbar emits on purpose. A live autocomplete query
 * and a catalogue binding must not: padding would make the query unmatchable
 * against the catalogue, and a padded binding would serialize to a token the
 * transformer never re-parses. Derived from the same fragment as the token
 * regex so the two cannot drift.
 */
export function isFactTokenBody(text: string): boolean {
  return FACT_BODY_REGEX.test(text)
}

const TOKEN_SOURCE = TOKEN_FORM_SOURCES.join('|')

/**
 * Global regex used by the mdast transformer to find every token inside a text
 * node. Global (`g`) is stateful — callers MUST reset `lastIndex = 0` before
 * each new string.
 */
export function createTokenRegex(): RegExp {
  return new RegExp(TOKEN_SOURCE, 'gi')
}

/**
 * Anchored (non-global) regex for the live-typing input rule: matches a token
 * that ends exactly at the cursor (closing delimiter just typed). Each form
 * already ends in its own closing delimiter, so anchoring with `$` fires the
 * rule only on the final keystroke that completes a token.
 */
export function createInputRuleRegex(): RegExp {
  return new RegExp(`(?:${TOKEN_SOURCE})$`, 'i')
}

/**
 * Fact vs jinja by the OPENING delimiter — a single `{` NOT followed by
 * `{`/`#`/`%`. Loose on purpose so an empty/whitespace shell (`{ }`, `{}`) still
 * colors as a fact while it is being filled; the nodeView keys its modifier
 * class off this (fact → primary-solid, jinja → pink-10).
 */
export function opensAsFact(value: string): boolean {
  return /^\{(?![{#%])/.test(value)
}

/**
 * True if `value` is EXACTLY one complete token — both delimiters intact, no
 * surrounding text. The inline editor uses this to decide whether an edit still
 * yields a token: break a delimiter (`{{ x }}` → `{ x }}`, or strip both braces)
 * and this turns false, so the editor dissolves the pill back to plain prose
 * rather than keeping a malformed atom that can never round-trip.
 */
const COMPLETE_TOKEN_RE = new RegExp(`^(?:${TOKEN_SOURCE})$`, 'i')

export function isCompleteToken(value: string): boolean {
  return COMPLETE_TOKEN_RE.test(value)
}

/** A token whose delimiters are present but the interior is empty/whitespace. */
export function isDelimiterShell(text: string): boolean {
  return (
    /^\{\{\s*\}\}$/.test(text) || // {{  }}
    /^\{#\s*#\}$/.test(text) || // {# #}
    /^\{%\s*%\}$/.test(text) || // {% %}
    /^\{\s*\}$/.test(text) // {} / {  }
  )
}
